/**
 * usePermission – Hook kiểm tra quyền theo role hiện tại
 * v2: Đọc từ DB (permissions_json) + merge multi-role
 * Wrapper trên useMergedPermissions
 * CRITICAL FIX: Admin bypass – admin luôn có full quyền mọi module/action
 * PREVIEW MODE: Khi admin đang preview role, can() dùng previewRole thay vì real role
 */
import { useCallback } from 'react';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';
import { hasPermission, permissionMatrixConfig } from '@/config/permission-matrix.config';
import type { Module, Action } from '@/config/permission-matrix.config';
import type { UserRole } from '@/config/navigation.config';
import { useAdminRolePreviewContext } from '@/core/roles/AdminRolePreviewContext';
import { useMergedPermissions } from './hooks/useMergedPermissions';

export interface UsePermissionReturn {
  /** Kiểm tra 1 action cụ thể */
  can: (module: Module, action: Action) => boolean;
  /** Kiểm tra nhiều action (phải có TẤT CẢ) */
  canAll: (module: Module, actions: Action[]) => boolean;
  /** Kiểm tra nhiều action (có ÍT NHẤT 1) */
  canAny: (module: Module, actions: Action[]) => boolean;
  /** Kiểm tra role */
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  /** Role hiện tại */
  role: UserRole;
  /** Có phải admin không */
  isAdmin: boolean;
  /** Có phải director không */
  isDirector: boolean;
  /** Toàn bộ permissions của role (đã merge từ DB) */
  permissions: Partial<Record<Module, Action[]>>;
  /** Đang loading permissions */
  isLoading: boolean;
  /** Force refresh permissions */
  refresh: () => void;
}

export function usePermission(): UsePermissionReturn {
  const { user } = useCurrentUser();
  const { previewRole, isPreviewMode } = useAdminRolePreviewContext();
  const { can: mergedCan, canAll: mergedCanAll, canAny: mergedCanAny, permissions, isLoading, refresh } = useMergedPermissions();

  const realRole = user.roleCode;
  const isAdmin = ['admin', 'super_admin'].includes(realRole);

  // Khi admin đang preview role: dùng previewRole cho can() checks
  // Nhưng isAdmin vẫn = true (admin vẫn là admin thật)
  const role: UserRole = isAdmin && isPreviewMode && previewRole ? previewRole : realRole;

  const can = useCallback(
    (module: Module, action: Action): boolean => {
      if (isAdmin) return true; // Admin bypass
      return mergedCan(module, action);
    },
    [isAdmin, mergedCan]
  );

  const canAll = useCallback(
    (module: Module, actions: Action[]): boolean => {
      if (isAdmin) return true;
      return mergedCanAll(module, actions);
    },
    [isAdmin, mergedCanAll]
  );

  const canAny = useCallback(
    (module: Module, actions: Action[]): boolean => {
      if (isAdmin) return true;
      return mergedCanAny(module, actions);
    },
    [isAdmin, mergedCanAny]
  );

  const hasRoleFn = useCallback(
    (roles: UserRole | UserRole[]): boolean => {
      const arr = Array.isArray(roles) ? roles : [roles];
      return arr.includes(role);
    },
    [role]
  );

  return {
    can,
    canAll,
    canAny,
    hasRole: hasRoleFn,
    role,
    isAdmin,
    isDirector: role === 'director',
    permissions,
    isLoading,
    refresh,
  };
}
